import Api from "../services/AxiosService";
import { unwrap } from "./envelope";

// svc-04 `/v1/admin/products*` admin CRUD over products_with_key_characteristics
// + the bulk-replaced products_key_characteristics child set. Types mirror
// @penn/shared-types `ProductAdmin` / `PaginatedProductsAdmin` (kept local per
// the existing api/*.ts convention — see machineOptions.ts).

export interface ProductAdmin {
  productId: string;
  rowNumber: number | null;
  category: string | null;
  subcategory: string | null;
  productName: string | null;
  link: string | null;
  responsibility: string | null;
  supportsPemClinch: boolean | null;
  supportsBlindRivet: boolean | null;
  supportsProfile: boolean | null;
  supportsOther: boolean | null;
  maxSizeLabel: string | null;
  maxSize: number | null;
  sizesMaxMetricNutSize: number | null;
  sizesMaxMetricStudSize: number | null;
  /** numeric(8,2) — decimal string. */
  maxForceKn: string | null;
  featuresProcessMonitoring: boolean | null;
  featuresRobotIntegration: boolean | null;
  availabilityEu: boolean | null;
  availabilityNa: boolean | null;
  availabilityAsia: boolean | null;
  costModelLabourCostPerPanel: string | null;
  costModelCalculationCostPerPanelFormula: string | null;
  costModelMachineTimePerPanel: string | null;
  costModelTotalInvestForApplication: string | null;
  costModelTypicalInsertionsLifetime: string | null;
  costModelMachineCostPerPanel: string | null;
  costModelQualityCostPerPanel: string | null;
  costModelOperatorTimePerPanel: string | null;
  costModelInstallationTimePerFastenerSec: string | null;
  imageLink: string | null;
  modelId: string | null;
  keyCharacteristics: string[];
  createdAt: string;
  updatedAt: string;
}

export interface ProductsPaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PaginatedProductsAdmin {
  data: ProductAdmin[];
  pagination: ProductsPaginationMeta;
}

export interface ListProductsAdminParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?:
    | "product_id"
    | "row_number"
    | "category"
    | "subcategory"
    | "product_name"
    | "created_at"
    | "updated_at";
  sortDir?: "asc" | "desc";
}

/** POST body — same shape as ProductAdmin minus server-managed fields. */
export interface CreateProductAdminBody {
  productId: string;
  rowNumber?: number | null;
  category?: string | null;
  subcategory?: string | null;
  productName?: string | null;
  link?: string | null;
  responsibility?: string | null;
  supportsPemClinch?: boolean | null;
  supportsBlindRivet?: boolean | null;
  supportsProfile?: boolean | null;
  supportsOther?: boolean | null;
  maxSizeLabel?: string | null;
  maxSize?: number | null;
  sizesMaxMetricNutSize?: number | null;
  sizesMaxMetricStudSize?: number | null;
  maxForceKn?: string | number | null;
  featuresProcessMonitoring?: boolean | null;
  featuresRobotIntegration?: boolean | null;
  availabilityEu?: boolean | null;
  availabilityNa?: boolean | null;
  availabilityAsia?: boolean | null;
  costModelLabourCostPerPanel?: string | number | null;
  costModelCalculationCostPerPanelFormula?: string | null;
  costModelMachineTimePerPanel?: string | number | null;
  costModelTotalInvestForApplication?: string | number | null;
  costModelTypicalInsertionsLifetime?: string | number | null;
  costModelMachineCostPerPanel?: string | number | null;
  costModelQualityCostPerPanel?: string | number | null;
  costModelOperatorTimePerPanel?: string | number | null;
  costModelInstallationTimePerFastenerSec?: string | number | null;
  imageLink?: string | null;
  modelId?: string | null;
  keyCharacteristics?: string[];
}

export type UpdateProductAdminBody = Partial<Omit<CreateProductAdminBody, "productId">>;

export interface CategoryOption {
  categoryId: string;
  name: string;
}

export interface SubcategoryOption {
  subcategoryId: string;
  name: string;
  category: string | null;
}

export interface CostModelOption {
  modelId: string;
  label: string | null;
}

const BASE = "/v1/admin/products";

/** GET /v1/admin/products — paginated list. */
export async function listProductsAdmin(
  params: ListProductsAdminParams = {},
): Promise<PaginatedProductsAdmin> {
  const res = await Api.get(BASE, { params });
  return unwrap<PaginatedProductsAdmin>(res.data);
}

/** GET /v1/admin/products/:id — for the View / Edit prefill. */
export async function getProductAdmin(productId: string): Promise<ProductAdmin> {
  const res = await Api.get(`${BASE}/${encodeURIComponent(productId)}`);
  return unwrap<ProductAdmin>(res.data);
}

/** POST /v1/admin/products. */
export async function createProductAdmin(
  body: CreateProductAdminBody,
): Promise<ProductAdmin> {
  const res = await Api.post(BASE, body);
  return unwrap<ProductAdmin>(res.data);
}

/** PUT /v1/admin/products/:id. */
export async function updateProductAdmin(
  productId: string,
  body: UpdateProductAdminBody,
): Promise<ProductAdmin> {
  const res = await Api.put(`${BASE}/${encodeURIComponent(productId)}`, body);
  return unwrap<ProductAdmin>(res.data);
}

/** DELETE /v1/admin/products/:id — soft-delete. */
export async function deleteProductAdmin(productId: string): Promise<{ success: true }> {
  const res = await Api.delete(`${BASE}/${encodeURIComponent(productId)}`);
  return unwrap<{ success: true }>(res.data);
}

/** GET /v1/admin/products/categories — dropdown source. */
export async function listProductCategories(): Promise<CategoryOption[]> {
  const res = await Api.get(`${BASE}/categories`);
  return unwrap<CategoryOption[]>(res.data);
}

/** GET /v1/admin/products/subcategories?category= — dropdown source. */
export async function listProductSubcategories(
  category?: string,
): Promise<SubcategoryOption[]> {
  const res = await Api.get(`${BASE}/subcategories`, {
    params: category ? { category } : {},
  });
  return unwrap<SubcategoryOption[]>(res.data);
}

/** GET /v1/admin/products/cost-models — Cost Model dropdown source. */
export async function listCostModels(): Promise<CostModelOption[]> {
  const res = await Api.get(`${BASE}/cost-models`);
  return unwrap<CostModelOption[]>(res.data);
}
